import { useMemo, useState } from "react";
import { useLogoStore } from "../../state/logoStore";
import { renderLogoSVG } from "../../engine/render";
import { generateIdeas, ideaToStatePatch, type DesignIdea } from "../../engine/ideaGenerator";
import { gradeClass } from "./EditorControls";

type RemixMode = "all" | "icon" | "colors" | "layout";

/**
 * "More ideas" tab — variations focused on the user's CURRENT design.
 *
 * Suggested = broad exploration. More Ideas = exploit + remix.
 * Filters let you fix one axis (icon / colors / layout) and vary the rest.
 */
export function MoreIdeasPanel() {
  const s = useLogoStore();
  const [mode, setMode] = useState<RemixMode>("all");
  const [seed, setSeed] = useState(0);

  const remixes = useMemo(() => {
    const allIdeas = generateIdeas({
      title: s.title,
      slogan: s.slogan,
      referencePalette: s.referencePalette,
      customIcons: s.customIcons,
      preferIconId: s.iconId,
      count: 80,
      seed,
    });

    let filtered = allIdeas;
    if (mode === "icon" && s.iconId) {
      filtered = allIdeas.filter((i) => i.iconId === s.iconId);
    } else if (mode === "colors") {
      filtered = allIdeas.filter((i) =>
        i.background === s.background &&
        i.titleColor === s.titleColor &&
        i.iconColor === s.iconColor
      );
    } else if (mode === "layout") {
      filtered = allIdeas.filter((i) => i.layout === s.layout);
    }

    // Drop ideas that match the current design exactly (boring)
    filtered = filtered.filter((i) =>
      !(i.iconId === s.iconId &&
        i.layout === s.layout &&
        i.background === s.background &&
        i.iconColor === s.iconColor)
    );

    if (filtered.length === 0) filtered = allIdeas;
    return filtered.slice(0, 20);
  }, [
    s.title, s.slogan, s.iconId, s.layout, s.background, s.titleColor, s.iconColor,
    s.referencePalette, s.customIcons, mode, seed,
  ]);

  function applyIdea(idea: DesignIdea) {
    s.pushHistory();
    s.setStyle(ideaToStatePatch(idea) as Parameters<typeof s.setStyle>[0]);
    s.setIcon(idea.iconId);
  }

  return (
    <>
      <div className="ideas-head">
        <div>
          <div className="ideas-title">More ideas for your logo</div>
          <div className="ideas-sub">Variations on what you have. Try a remix mode below.</div>
        </div>
        <button className="btn ghost btn-sm" onClick={() => setSeed((v) => v + 1)} title="Reshuffle">↻</button>
      </div>

      <div className="cat-row" style={{ marginBottom: 12 }}>
        <button className={`chip ${mode === "all" ? "active" : ""}`} onClick={() => setMode("all")}>All</button>
        <button className={`chip ${mode === "icon" ? "active" : ""}`} onClick={() => setMode("icon")} disabled={!s.iconId} title={s.iconId ? "Keep current icon, vary everything else" : "Pick a symbol first"}>Same icon</button>
        <button className={`chip ${mode === "colors" ? "active" : ""}`} onClick={() => setMode("colors")} title="Keep current colors, vary icon + layout">Same colors</button>
        <button className={`chip ${mode === "layout" ? "active" : ""}`} onClick={() => setMode("layout")} title="Keep current layout, vary icon + colors">Same layout</button>
      </div>

      <div className="ideas-grid">
        {remixes.map((idea, i) => {
          const svg = renderLogoSVG(
            { title: s.title || "Your Brand", slogan: s.slogan || "tagline", ...idea, qrText: null } as Parameters<typeof renderLogoSVG>[0],
            { width: 240, height: 240, customIcons: s.customIcons }
          );
          return (
            <button
              key={i}
              className="idea-card"
              onClick={() => applyIdea(idea)}
              title={`Apply this variation · score ${idea.score.scorePercent}/100 (${idea.score.grade})`}
            >
              <div className="idea-svg" dangerouslySetInnerHTML={{ __html: svg }} />
              <div className={`idea-grade grade-${gradeClass(idea.score.grade)}`}>{idea.score.grade}<span> {idea.score.scorePercent}</span></div>
            </button>
          );
        })}
      </div>

      {remixes.length === 0 && (
        <div style={{ padding: "20px 0", fontSize: 12, color: "#94a3b8", textAlign: "center" }}>
          No variations match the selected filter. Try "All" or change your design first.
        </div>
      )}
    </>
  );
}
